import type { ProgressReport } from '@light2live/shared-types';
import { burdenSeries, formatShortDate, rowChanges } from '@/lib/progress-trend';

/**
 * Whether the client's symptom burden is going down, across all 19 rows.
 *
 * Every row is scored through the shared `concernOfFrequency`, so the five
 * emotion rows where more is better — content, peaceful and calm, compassion
 * with myself, thankful for my life, optimistic about my progress — reduce the
 * score when they rise. Summing raw frequencies instead would tell a client who
 * became more thankful that they had got worse.
 *
 * Bars rather than an SVG: a proportion of a fixed maximum is what a bar is
 * for, and this way every number is real text a screen reader already reads.
 */
export function SymptomBurden({ reports }: { reports: ProgressReport[] }) {
    const series = burdenSeries(reports);
    const changes = rowChanges(reports);

    const first = series[0];
    const last = series[series.length - 1];
    const max = last.max;

    // concernDelta is positive when concern went UP, i.e. worse for the client.
    const improved = changes
        .filter((row) => row.concernDelta < 0)
        .sort((a, b) => a.concernDelta - b.concernDelta)
        .slice(0, 3);

    const worsened = changes
        .filter((row) => row.concernDelta > 0)
        .sort((a, b) => b.concernDelta - a.concernDelta)
        .slice(0, 3);

    const overall = last.total - first.total;

    return (
        <section aria-labelledby="burden-heading">
            <h3
                id="burden-heading"
                className="text-sm font-semibold tracking-wide text-gray-700 uppercase"
            >
                How you have been feeling overall
            </h3>

            <p className="mt-2 text-gray-700">
                {series.length < 2 ? (
                    <>
                        Your symptom score is{' '}
                        <strong className="text-gray-900">
                            {last.total} out of {max}
                        </strong>
                        . Lower is better. Your next report will show whether it is moving.
                    </>
                ) : overall < 0 ? (
                    <>
                        Your symptom score has come{' '}
                        <strong className="text-brand-text-green">
                            down by {Math.abs(overall)}
                        </strong>
                        , from {first.total} to {last.total} out of {max}. Lower is better, so this
                        is going the right way.
                    </>
                ) : overall > 0 ? (
                    <>
                        Your symptom score has gone{' '}
                        <strong className="text-amber-700">up by {overall}</strong>, from{' '}
                        {first.total} to {last.total} out of {max}. Worth mentioning to your coach
                        at your next session.
                    </>
                ) : (
                    <>
                        Your symptom score is unchanged at{' '}
                        <strong className="text-gray-900">
                            {last.total} out of {max}
                        </strong>
                        .
                    </>
                )}
            </p>

            <ol className="mt-4 space-y-2">
                {series.map((point) => (
                    <li key={point.index} className="flex items-center gap-3">
                        <span className="w-14 shrink-0 text-xs text-gray-600">
                            {formatShortDate(point.reportDate)}
                        </span>
                        {/* The track is the brand's, the fill is deliberately
                            not: this bar is a burden, where a longer bar is bad
                            news, and #94c740 is the colour the product uses for
                            progress made. A lime burden bar would say the
                            opposite of what it means. */}
                        <span className="h-3 flex-1 overflow-hidden rounded-full bg-brand-bar-track">
                            <span
                                className="block h-full rounded-full bg-gray-700"
                                style={{ width: `${(point.total / point.max) * 100}%` }}
                            />
                        </span>
                        <span className="w-14 shrink-0 text-right text-xs text-gray-700 tabular-nums">
                            {point.total} / {point.max}
                        </span>
                    </li>
                ))}
            </ol>

            {(improved.length > 0 || worsened.length > 0) && (
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <RowList
                        title="Most improved"
                        empty="Nothing has improved since your first report yet."
                        rows={improved}
                    />
                    <RowList
                        title="Worth a mention to your coach"
                        empty="Nothing has got worse since your first report."
                        rows={worsened}
                    />
                </div>
            )}
        </section>
    );
}

function RowList({
    title,
    empty,
    rows,
}: {
    title: string;
    empty: string;
    rows: { label: string; concernDelta: number }[];
}) {
    return (
        <div>
            <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
            {rows.length === 0 ? (
                <p className="mt-1 text-sm text-gray-600">{empty}</p>
            ) : (
                <ul className="mt-1 space-y-1">
                    {rows.map((row) => (
                        <li key={row.label} className="text-sm text-gray-700">
                            {row.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
